import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

export const threejs = () => {

	let el = document.getElementById('canvas')

	if (!el) return

    let scene, camera, renderer, model;
    
    // Camera orbital controls
    let mouseX = 0, mouseY = 0;
    let targetMouseX = 0, targetMouseY = 0;
    let cameraDistance = 8;
    
    // Initialize the scene
    function init() {
        // Scene setup
        scene = new THREE.Scene();
        scene.background = new THREE.Color(0xffffff);
        
        // Camera setup - reduced FOV to minimize fisheye effect
        camera = new THREE.PerspectiveCamera(35, window.innerWidth / window.innerHeight, 0.1, 1000);
        camera.position.set(0, 0, cameraDistance);
        
        // Renderer setup
        renderer = new THREE.WebGLRenderer({ 
            canvas: el,
            antialias: true,
            alpha: true
        });
        renderer.setSize(window.innerWidth, window.innerHeight);
        renderer.setPixelRatio(window.devicePixelRatio);
        renderer.shadowMap.enabled = true;
        renderer.shadowMap.type = THREE.PCFSoftShadowMap;
        renderer.toneMapping = THREE.ACESFilmicToneMapping;
        renderer.toneMappingExposure = 1.2;
        
        // Lighting setup
        setupLighting();
        
        // Load the 3D model
        loadModel();
        
        // Event listeners
        window.addEventListener('resize', onWindowResize);
        window.addEventListener('mousemove', onMouseMove);
        
        // Start animation loop
        animate();
    }
    
    function setupLighting() {
        // Ambient light for overall illumination (brighter to lighten shadows)
        const ambientLight = new THREE.AmbientLight(0xffffff, 1.3);
        scene.add(ambientLight);
        
        // Main directional light positioned behind and above the camera (slightly reduced for softer shadows)
        const directionalLight = new THREE.DirectionalLight(0xffffff, 0.9);
        directionalLight.position.set(0, 5, 10);
        directionalLight.castShadow = true;
        directionalLight.shadow.mapSize.width = 2048;
        directionalLight.shadow.mapSize.height = 2048;
        scene.add(directionalLight);
        
        // Fill light from the opposite side (much brighter to fill shadows)
        const fillLight = new THREE.DirectionalLight(0xffffff, 0.8);
        fillLight.position.set(-5, -3, 8);
        scene.add(fillLight);
        
        // Rim light for edge definition (maintained)
        const rimLight = new THREE.DirectionalLight(0xffffff, 0.9);
        rimLight.position.set(3, 8, 12);
        scene.add(rimLight);
    }
    
    function loadModel() {
        const loader = new GLTFLoader();
        
        // Create a simple fallback geometry if model fails to load (smaller)
        const geometry = new THREE.BoxGeometry(1.5, 1.5, 1.5);
        const material = new THREE.MeshPhysicalMaterial({
            color: 0xffffff,
            metalness: 0.0,
            roughness: 0.05,
            clearcoat: 0.8,
            clearcoatRoughness: 0.02,
            reflectivity: 0.5,
            envMapIntensity: 0.8,
            transmission: 0.0,
            emissive: 0x222222,
            emissiveIntensity: 0.1
        });
        
        model = new THREE.Mesh(geometry, material);
        model.castShadow = true;
        model.receiveShadow = true;
        
        // Initial rotations: 90 degrees on X axis, 270 degrees on Y axis (additional 90 degrees on Y)
        model.rotation.x = Math.PI / 2;
        model.rotation.y = Math.PI * 1.5;
        
        scene.add(model);
        
        // Try to load the actual model, but don't fail if it's not available
        loader.load(
            canvas.dataset.shape,
            function(gltf) {
                // Remove the fallback geometry
                scene.remove(model);
                
                model = gltf.scene;
                
                // Create whiter and brighter material
                const glossyMaterial = new THREE.MeshPhysicalMaterial({
                    color: 0xffffff,
                    metalness: 0.0,
                    roughness: 0.05,
                    clearcoat: 0.8,
                    clearcoatRoughness: 0.02,
                    reflectivity: 0.5,
                    envMapIntensity: 0.8,
                    transmission: 0.0,
                    emissive: 0x222222,
                    emissiveIntensity: 0.1
                });
                
                // Apply material to all meshes
                model.traverse((child) => {
                    if (child.isMesh) {
                        child.material = glossyMaterial;
                        child.castShadow = true;
                        child.receiveShadow = true;
                    }
                });
                
                // Scale the model (smaller)
                const box = new THREE.Box3().setFromObject(model);
                const size = box.getSize(new THREE.Vector3());
                const maxSize = Math.max(size.x, size.y, size.z);
                const scale = 8 / maxSize;
                model.scale.setScalar(scale);
                
                // Initial rotations: 90 degrees on X axis, 270 degrees on Y axis (additional 90 degrees on Y)
                model.rotation.x = Math.PI / 2;
                model.rotation.y = Math.PI * 1.5;
                
                scene.add(model);
            },
            function(progress) {
                // console.log('Loading progress:', (progress.loaded / progress.total * 100) + '%');
            },
            function(error) {
                // console.log('Using fallback geometry - model not found');
            }
        );
    }
    
    function onMouseMove(event) {
        // Convert mouse position to normalized coordinates (-1 to 1)
        targetMouseX = (event.clientX / window.innerWidth) * 2 - 1;
        targetMouseY = -(event.clientY / window.innerHeight) * 2 + 1;
    }
    
    function onWindowResize() {
        camera.aspect = window.innerWidth / window.innerHeight;
        camera.updateProjectionMatrix();
        renderer.setSize(window.innerWidth, window.innerHeight);
    }
    
    function updateCamera() {
        // Smooth interpolation for mouse movement
        mouseX += (targetMouseX - mouseX) * 0.05;
        mouseY += (targetMouseY - mouseY) * 0.05;
        
        // Convert mouse position to spherical coordinates (much reduced sensitivity)
        const phi = mouseX * Math.PI * 0.08; // Horizontal rotation (very subtle)
        const theta = (mouseY * 0.08 + 0.5) * Math.PI; // Vertical rotation (very subtle)
        
        // Calculate camera position in spherical coordinates
        const x = cameraDistance * Math.sin(theta) * Math.cos(phi);
        const y = cameraDistance * Math.cos(theta);
        const z = cameraDistance * Math.sin(theta) * Math.sin(phi);
        
        // Set camera position
        camera.position.set(x, y, z);
        
        // Make camera look at the center (where the model is)
        camera.lookAt(0, 0, 0);
    }
    
    function animate() {
        requestAnimationFrame(animate);
        
        // Update camera position based on mouse
        updateCamera();
        
        // Model stays at fixed rotation (no automatic rotation)
        if (model) {
            // Keep the model at its initial rotation (90 degrees on X, 270 degrees on Y)
            // No automatic rotation applied
            model.rotation.x = Math.PI * 0.5;
            model.rotation.y = 0;
            model.rotation.z = Math.PI * -0.5;
            
            // No floating motion either - model stays stationary
            // model.position.y = 0;
        }
        
        renderer.render(scene, camera);
    }
    
    // Initialize when called
    init();
}

export default () => { }